import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Checkbox, Grid, MenuItem, Select, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import PaymayaSdkClient from 'paymaya-js-sdk';
import PropTypes from 'prop-types';

import { RHFTextField } from '../../../../components/hook-form';
import { CPO_URL, MAYA_KEY } from '../../../../config';
import { DonationAPI } from '../../../../services/donation.services';
import NoAmountModal from '../NoAmountModal';
import { getPlan } from '../../../../services/paypal.services';

const REDIRECT_URL = `${CPO_URL}/resultpage`;

SimplifiedCardPaymentForm.propTypes = {
  projects: PropTypes.array.isRequired,
  currency: PropTypes.string,
  onSuccess: PropTypes.func,
  actionRef: PropTypes.any,
  resolverRef: PropTypes.any,
  methods: PropTypes.any,
  setIsRecurring: PropTypes.func,
  setIsLoading: PropTypes.func,
  isDiscretionary: PropTypes.bool,
  onRedirect: PropTypes.func,
};

export default function SimplifiedCardPaymentForm({
  projects,
  currency = 'PHP',
  onSuccess,
  actionRef,
  resolverRef,
  methods,
  setIsRecurring,
  setIsLoading,
  isDiscretionary,
  onRedirect,
}) {
  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState('monthly');
  const [isAnon, setIsAnon] = useState(false);
  const [noAmountOpen, setNoAmountOpen] = useState(false);

  const resolver = yupResolver(
    Yup.object().shape({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      phone: Yup.string().required('Required'),
      email: Yup.string().required('Required'),
    })
  );

  useEffect(() => {
    resolverRef.current = resolver;

    return () => {
      setIsRecurring(false);
    };
  }, []);

  const defaultValues = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  };

  const { reset } = methods;

  const onSubmitMaya = async (data, projects) => {
    PaymayaSdkClient.init(MAYA_KEY, false);
    const amount = projects.reduce((prev, val) => prev + val.amount, 0);
    const noAmountProject = projects.filter((e) => !e.amount);

    if (!amount || noAmountProject.length) {
      setNoAmountOpen(true);
      return;
    }

    const payload = createPayload({ amount, formData: data, projects, currency, discretionary: isDiscretionary });
    setIsLoading(true);
    DonationAPI.submitDonationMaya(payload)
      .then((e) => {
        const refId = e.data.data.insertedId;
        const refDataParam = btoa(
          JSON.stringify({
            amount,
            refId,
            currency,
          })
        );

        const checkoutDetails = createCheckoutDetails({
          amount,
          data,
          projects,
          refDataParam,
          currency,
          requestReferenceNumber: e.data.data.insertedId,
        });

        localStorage.setItem('checkoutDetails', JSON.stringify(checkoutDetails));

        onSuccess?.();


        setIsLoading(false);

        const redirect = async () => {
          await PaymayaSdkClient.createCheckout(checkoutDetails);
          setTimeout(() => {
            onRedirect?.();
          }, 2000);
        }

        redirect();

      })
      .catch((e) => {
        alert('Encountered an error. Please try again.');
      });
  };

  const onSubmitPaypal = async (data, projects) => {
    const amount = projects.reduce((prev, val) => prev + val.amount, 0);
    const noAmountProject = projects.filter((e) => !e.amount);

    if (!amount || noAmountProject.length) {
      setNoAmountOpen(true);
    }

    setIsLoading(true);
    const payload = createPayload({
      amount,
      formData: data,
      projects,
      frequency,
      currency,
      discretionary: isDiscretionary,
    });
    DonationAPI.submitDonationPaypal(payload).then(async (e) => {
      const planId = await getPlanId();
      const refId = e.data.data.insertedId;
      setIsRecurring(true);
      setIsLoading(false);
      document.getElementById('paypal-button-container').innerHTML = '';
      const { paypal } = window;
      paypal
        .Buttons({
          createSubscription: (data, actions) =>
            actions.subscription.create({
              plan_id: planId.data,
              custom_id: refId,
            }),
          onApprove: () => {
            onSuccess?.();
          },
        })
        .render('#paypal-button-container');
    });
  };

  const anonymous = {
    firstName: 'Anonymous',
    lastName: 'Donor',
    email: 'anonymousdonor@charityphilippines.org',
    phone: '09111111111',
  };

  useEffect(() => {
    if (isAnon) {
      reset(anonymous);
    } else {
      reset(defaultValues);
    }
  }, [isAnon]);

  const getPlanId = () => {
    const amount = projects.reduce((prev, val) => prev + val.amount, 0);
    const noAmountProject = projects.filter((e) => !e.amount);

    if (amount && !noAmountProject.length && frequency) {
      return getPlan({ projects, frequency, currency });
    }
  };

  useEffect(() => {
    if (recurring) {
      setIsAnon(false);
      actionRef.current = onSubmitPaypal;
    } else {
      actionRef.current = onSubmitMaya;
    }
  }, [recurring]);

  return (
    <Box component="form" noValidate sx={{ mt: 1, px: { md: 3, xs: 0 } }}>
      <Grid container spacing={2} direction="column">
        <Grid
          item
          container
          alignItems="center"
          mt={3}
          sx={{ cursor: 'pointer', p: '0 !important' }}
          onClick={() => setRecurring(!recurring)}
        >
          <Grid item xs={1.5} md={0.6}>
            <Checkbox checked={recurring} onChange={() => setRecurring(!recurring)} />
          </Grid>
          <Grid item width="fit-content" xs={10}>
            <Typography>I want to make a greater impact by setting up a recurring donation.</Typography>
          </Grid>
        </Grid>
        {recurring ? (
          <Box my={2} pl={1}>
            <Typography fontStyle="italic" fontSize={14}>
              Frequency:
            </Typography>
            <Select
              value={frequency}
              inputProps={{ sx: { width: { md: 300, xs: 150 }, bgcolor: 'white' } }}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annually">Annually</MenuItem>
            </Select>
          </Box>
        ) : (
          <Grid
            item
            container
            alignItems="center"
            sx={{ cursor: 'pointer', p: '0 !important' }}
            onClick={() => setIsAnon(!isAnon)}
          >
            <Grid item>
              <Checkbox checked={isAnon} onChange={() => setIsAnon(!isAnon)} />
            </Grid>
            <Grid item width="fit-content">
              <Typography>Anonymous Donor</Typography>
            </Grid>
          </Grid>
        )}
        {isAnon ? null : (
          <Box mt={5} mb={2}>
          <Grid item container spacing={2} justifyContent="space-between">
            <Grid item xs={12} md={6}>
              <RHFTextField name="firstName" fullWidth label="First Name" />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name="lastName" fullWidth label="Last Name" />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name="email" fullWidth label="Email" />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name="phone" fullWidth label="Phone Number" />
            </Grid>
          </Grid>
          </Box>
        )}
      </Grid>
      {/* <Grid container justifyContent="center" py={5}>
          <Box width={300}>
            {!recurring && (
              <Button disabled={disabled} fullWidth variant="contained" size="large" type="submit">
                SUBMIT
              </Button>
            )}
            {recurring && (
              <div id="paypal-button-container">
                <LoadingButton loading={isGettingPlanId} fullWidth variant="contained" size="large" type="submit">
                  PROCEED TO PAYMENT
                </LoadingButton>
              </div>
            )}
          </Box>
        </Grid> */}
      <NoAmountModal open={noAmountOpen} onClose={() => setNoAmountOpen(false)} />
    </Box>
  );
}

const createPayload = ({ discretionary, amount, formData, projects, causes = [], currency, frequency, giftCard }) => ({
  firstName: formData.firstName,
  lastName: formData.lastName,
  email: formData.email,
  contactNumber: formData.phone,
  address: '',
  country: '',
  website: '',
  donationAmount: amount,
  currency,
  projects: projects.map((e) => ({
    id: e.id ?? e._id,
    name: e.name,
    amount: e.amount,
    charityId: e.charityDetails?.id ?? e.charityId,
    charityName: 'CharityPhilippines.org',
  })),
  discretion: {
    isDiscretion: discretionary,
    causes,
    frequency,
  },
  giftCard,
});

const createCheckoutDetails = ({ amount, data, projects, refDataParam, requestReferenceNumber, currency = 'PHP' }) => ({
  totalAmount: {
    value: amount,
    currency,
    details: {
      discount: 0,
      serviceCharge: 0,
      shippingFee: 0,
      subtotal: amount,
      tax: 0,
    },
  },
  buyer: {
    firstName: data.firstName,
    lastName: data.lastName,
    contact: {
      email: data.email,
      phone: data.phone,
    },
  },
  items: projects.map((e) => ({
    name: e.name,
    quantity: 1,
    code: e.id ? e.id?.toString() : e._id?.toString(),
    description: e.shortDescription,
    amount: {
      value: e.amount,
      details: {
        discount: 0,
        serviceCharge: 0,
        shippingFee: 0,
        subtotal: e.amount,
      },
    },
    totalAmount: {
      value: e.amount,
      details: {
        discount: 0,
        serviceCharge: 0,
        shippingFee: 0,
        tax: 0,
        subtotal: e.amount,
      },
    },
  })),
  redirectUrl: {
    success: `${REDIRECT_URL}/${refDataParam}/success?redirectToHome=true`,
    failure: `${REDIRECT_URL}/${refDataParam}/failed?redirectToHome=true`,
    cancel: `${REDIRECT_URL}/${refDataParam}/cancelled?redirectToHome=true`,
  },
  requestReferenceNumber,
});
